import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Toast } from '@whelk/components';
import { isApiError } from '@whelk/services/src/common';
import { LocalUnionIDKey } from '@whelk/const/src/token';
import { postGetCaptchaInfo, postSendPhoneCode } from '@whelk/services/src/user';

declare let TencentCaptcha: any;

function SendCodeBtn({
  getPhone,
  openAppealModal,
  isPhoneValid,
}: {
  getPhone: () => string;
  openAppealModal: () => void;
  isPhoneValid: boolean;
}) {
  const timer = useRef<number>();
  const [time, setTime] = useState(60);
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const handlingRef = useRef(false);

  useEffect(() => {
    if (sending) {
      timer.current = window.setInterval(() => {
        setTime((t) => {
          if (t === 0) {
            clearInterval(timer.current!);
            setSending(false);
            return 60;
          } else {
            return t - 1;
          }
        });
      }, 1000);
    } else {
      setTime(60);
      if (timer.current) {
        clearInterval(timer.current);
      }
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [sending]);

  const sendSms = async ({ ticket, randStr }: { ticket?: string; randStr?: string } = {}) => {
    const phone = getPhone();
    try {
      const payload: any = {
        phone,
        ticket,
        randStr,
      };
      if (localStorage.getItem(LocalUnionIDKey)) {
        payload.unionID = localStorage.getItem(LocalUnionIDKey) ?? '';
      }
      await postSendPhoneCode(payload);
      setSending(true);
      Toast.success(t('toast_verification_code_sent'));
      handlingRef.current = false;
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === 1200036) {
          openAppealModal();
        } else {
          Toast.error(err.message);
        }
      }
      setSending(false);
      handlingRef.current = false;
    }
  };

  const onCaptchaCallback = (res: any) => {
    if (res.ret === 2) {
      handlingRef.current = false;
      return;
    }
    if (res.ret === 0 && !res.errorCode && res.ticket && res.randstr) {
      sendSms({ ticket: res.ticket, randStr: res.randstr });
    } else {
      sendSms();
    }
  };

  const onCaptchaLoadError = () => {
    sendSms();
  };

  const onGetVerificationCode = async () => {
    if (sending || handlingRef.current) {
      return;
    }
    handlingRef.current = true;
    const phone = getPhone();
    if (!phone) {
      Toast.info(t('login_enter_phone_number'));
      handlingRef.current = false;
      return;
    }
    try {
      const { needCaptcha, aid, aidEncrypted } = await postGetCaptchaInfo({
        phone,
      });
      if (needCaptcha) {
        try {
          const captcha = new TencentCaptcha(aid, onCaptchaCallback, {
            aidEncrypted,
            userLanguage: navigator.language,
          });
          captcha.show();
        } catch (error) {
          onCaptchaLoadError();
        }
      } else {
        await sendSms();
      }
    } catch (error) {
      handlingRef.current = false;
    }
  };

  const sendText = !sending
    ? t('login_get_verification_code')
    : `${t('verification_code_resend')}   ${time}s`;

  const btnClass = classNames(
    'w-[110px] whitespace-nowrap rounded-br-[12px] rounded-tr-[12px]  text-center text-[14px] leading-[46px] ',
    sending || !isPhoneValid
      ? 'cursor-not-allowed text-col_brand00 opacity-40'
      : 'text-col_brand00',
  );

  return (
    <button type='button' onClick={onGetVerificationCode} className={btnClass}>
      {sendText}
    </button>
  );
}

export default SendCodeBtn;
